import { Shield, Users, Award, Truck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

const About = () => {
  const { t } = useLanguage();

  const stats = [
    { value: '15+', label: t.about.yearsExperience },
    { value: '10K+', label: t.about.satisfiedCustomers },
    { value: '500+', label: t.about.productsCount },
    { value: '24/7', label: t.about.support }
  ];

  const values = [
    { icon: Shield, title: t.about.qualityFirst, description: t.about.qualityDesc },
    { icon: Users, title: t.about.expertTeam, description: t.about.expertDesc },
    { icon: Award, title: t.about.professionalGrade, description: t.about.professionalDesc },
    { icon: Truck, title: t.about.fastDelivery, description: t.about.deliveryDesc }
  ];

  return (
    <div className="min-h-screen" data-testid="about-page">
      <section className="relative h-[50vh] min-h-[400px] flex items-center border-b border-zinc-800">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: 'url(https://images.pexels.com/photos/8159/pexels-photo.jpg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)' }} />
        <div className="absolute inset-0 bg-black/80" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <span className="font-mono text-xs tracking-[0.2em] uppercase text-yellow-500 mb-4 block">{t.about.title}</span>
          <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl tracking-tighter uppercase leading-[0.9] text-white max-w-3xl" data-testid="about-title">
            {t.about.heroTitle1}<br />
            <span className="text-yellow-400">{t.about.heroTitle2}</span>
          </h1>
        </div>
      </section>

      <section className="bg-[#111111] border-b border-zinc-800">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4">
            {stats.map((stat, index) => (
              <div key={index} className="p-8 md:p-12 text-center border-r border-b md:border-b-0 border-zinc-800 last:border-r-0 [&:nth-child(2)]:border-r-0 md:[&:nth-child(2)]:border-r">
                <div className="font-heading text-4xl md:text-5xl text-yellow-400 mb-2">{stat.value}</div>
                <div className="font-mono text-xs tracking-widest uppercase text-zinc-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="font-mono text-xs tracking-[0.2em] uppercase text-yellow-500 mb-4 block">{t.about.ourStory}</span>
              <h2 className="font-heading text-4xl sm:text-5xl tracking-tighter uppercase leading-none text-white mb-6">{t.about.trustedPartner}</h2>
              <div className="space-y-4 text-zinc-300 leading-relaxed">
                <p>{t.about.story1}</p>
                <p>{t.about.story2}</p>
                <p>{t.about.story3}</p>
              </div>
            </div>
            <div className="border border-zinc-800">
              <img src="https://images.pexels.com/photos/6106878/pexels-photo-6106878.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Construction site" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-[#111111] border-t border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="font-mono text-xs tracking-[0.2em] uppercase text-yellow-500 mb-4 block">{t.about.whyChooseUs}</span>
            <h2 className="font-heading text-4xl sm:text-5xl tracking-tighter uppercase leading-none text-white">{t.about.ourValues}</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
            {values.map((value, index) => (
              <div key={index} className="border border-zinc-800 p-8 md:p-12">
                <value.icon size={40} className="text-yellow-400 mb-6" strokeWidth={2} />
                <h3 className="font-heading text-2xl uppercase text-white mb-4">{value.title}</h3>
                <p className="text-zinc-400 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-4xl sm:text-5xl tracking-tighter uppercase leading-none text-white mb-6">{t.about.readyToStart}</h2>
          <p className="text-zinc-400 mb-8">{t.about.browseSelection}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/shop" className="bg-yellow-400 text-black uppercase font-bold tracking-widest px-8 py-4 border-2 border-yellow-400 hover:bg-black hover:text-yellow-400 transition-colors" data-testid="about-shop-btn">
              {t.home.shopNow}
            </Link>
            <Link to="/contact" className="bg-transparent text-white uppercase font-bold tracking-widest px-8 py-4 border-2 border-zinc-700 hover:border-white transition-colors" data-testid="about-contact-btn">
              {t.nav.contact}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
